import styled from "styled-components";
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector, shallowEqual } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCustomDataRedirect } from "../common/useCustomDataRedirect";
import AnalyticsBreadcrumb from "ProtoType/NewAnalytics/AnalyticsBreadcrumb";
import Filters from "../Filters";
import { filter_data } from "reports/Tag/calculations";
import { validateAndSanitizeOutcomes } from "../utils/outcomesDataIntegrity";
import { QUADRANT_KEYS, sanitizeQuadrantConfig } from "./quadrantUtils";
import {
  buildQuestionIdToPath,
  buildOutcomeQuestionSetByOutcomeName,
  computeQuadrantInclusionForRespondents,
  riskGradeForBottom2,
  scoreAnchorOptionForDimPercent,
} from "./lviShared";
import {
  extractEmployeeAuthDetails,
  parseAnalyticsPermissionsFromDetails,
} from "utilities/analyticsPermissions";

const BulkLviFromCsv = ({ core_data, outcomeQ: outcomeQProp, data, rule }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redirect protection for custom data from NewAnalytics
  useCustomDataRedirect();

  // Get employee and permission data for access control
  const { employee, permissionRecords, employeeId } = useSelector(
    (state) => ({
      employee: state.employees?.userEmp,
      permissionRecords: state.employee_permission?.employee_permission,
      employeeId: state.auth?.employee_id,
    }),
    shallowEqual
  );

  const selectedFilters = useSelector((state) => state.audit?.new_filters, shallowEqual);
  const { raw_data } = useSelector((state) => ({ raw_data: state.audit?.raw_data }), shallowEqual);
  const hasCustomData =
    raw_data && Array.isArray(raw_data) && raw_data.length > 0 && raw_data[0]?.datasetMetadata;

  // CRITICAL: Permission check - redirect users without custom_analytics permission
  useEffect(() => {
    const isAdmin = Number(employee?.account_type) === 5;
    if (isAdmin) return;

    if (employee?.access_data_analytics !== 1) {
      navigate("/leader/audit/new-analytics", { replace: true });
      return;
    }

    const authDetails = extractEmployeeAuthDetails(permissionRecords, employeeId);
    const parsedAnalytics = parseAnalyticsPermissionsFromDetails(authDetails);

    if (parsedAnalytics) {
      if (parsedAnalytics.custom_analytics !== true) {
        navigate("/leader/audit/new-analytics", { replace: true });
        return;
      }
    } else {
      navigate("/leader/audit/new-analytics", { replace: true });
      return;
    }
  }, [employee, permissionRecords, employeeId, navigate]);

  // Synchronous check to prevent rendering any content if unauthorized
  const isAdmin = Number(employee?.account_type) === 5;
  const hasBaseAnalyticsAccess = employee?.access_data_analytics === 1;
  const isAuthorized = (() => {
    if (isAdmin) return true;
    if (!hasBaseAnalyticsAccess) return false;
    const authDetails = extractEmployeeAuthDetails(permissionRecords, employeeId);
    const parsedAnalytics = parseAnalyticsPermissionsFromDetails(authDetails);
    return !!parsedAnalytics && parsedAnalytics.custom_analytics === true;
  })();

  const [correctedData, setCorrectedData] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [rowsComputed, setRowsComputed] = useState([]);
  const [isComputing, setIsComputing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [sortKey, setSortKey] = useState("risk"); // default like main LVI table
  const [sortDir, setSortDir] = useState("desc");
  const [groupMode, setGroupMode] = useState(false); // treat first column as Group ID and union rows per group
  const [splitTablesByLastCol, setSplitTablesByLastCol] = useState(false); // treat last column as Table Name (non-filter), 1 table per unique value
  const [activeTableKey, setActiveTableKey] = useState(""); // selected Table Name when splitTablesByLastCol enabled

  const [quadrantConfig, setQuadrantConfig] = useState(() =>
    sanitizeQuadrantConfig({
      xCutoff: 60,
      yCutoff: 60,
      subRect: { xmin: 0, xmax: 40, ymin: 0, ymax: 40 },
    })
  );
  const [riskThresholds, setRiskThresholds] = useState(() => ({
    veryHigh: 35,
    high: 30,
    moderate: 20,
    stable: 10,
    low: 5,
  }));

  // Prepare filtered data (respecting existing Filters component behavior)
  useEffect(() => {
    if (!data) return;
    let filteredData = [];

    if (Array.isArray(data) && data.length === 0 && selectedFilters.length === 0) return;
    if (Array.isArray(data) && data.length === 0) {
      setCorrectedData([]);
      return;
    }

    if (data?.[0]?.[0]) {
      filteredData = filter_data(data[0], selectedFilters);
      setCorrectedData(filteredData);
      return;
    }

    filteredData = filter_data(data, selectedFilters);
    setCorrectedData(filteredData);
  }, [data, selectedFilters]);

  // Prefer questions from custom dataset if present, else fall back to core_data.questions
  const questionsSource = useMemo(() => {
    if (hasCustomData && raw_data?.[0]?.question) return raw_data[0].question;
    return core_data?.questions;
  }, [hasCustomData, raw_data, core_data]);
  const analyticsSchema = questionsSource?.analytics;
  const analysisDef = analyticsSchema?.analyses;

  const minResponses = useMemo(
    () => Number(core_data?.Whitelabel?.rule ?? rule ?? 2),
    [core_data?.Whitelabel?.rule, rule]
  );

  // Pick the two dimensions for this analysis (prefer analysisDef.dimensions, fallback to first two schema dimensions)
  const [dim1Def, dim2Def] = useMemo(() => {
    const schemaDims = Array.isArray(analyticsSchema?.dimensions) ? analyticsSchema.dimensions : [];
    const analysisDims = Array.isArray(analysisDef?.dimensions) ? analysisDef.dimensions : [];

    const resolveDim = (analysisDim) => {
      if (!analysisDim) return null;
      const found = schemaDims.find((d) => String(d.key) === String(analysisDim.key));
      return found || null;
    };

    const d1 = resolveDim(analysisDims[0]) || schemaDims[0] || null;
    const d2 = resolveDim(analysisDims[1]) || schemaDims[1] || null;
    return [d1, d2];
  }, [analyticsSchema?.dimensions, analysisDef?.dimensions]);

  const dim1Name = dim1Def?.name?.en || dim1Def?.key || "Dimension 1";
  const dim2Name = dim2Def?.name?.en || dim2Def?.key || "Dimension 2";

  // Sanitize outcomeQ to avoid duplicate IDs / malformed sections
  const outcomeQ = useMemo(() => {
    if (!outcomeQProp) return null;
    try {
      const { sanitized } = validateAndSanitizeOutcomes(outcomeQProp);
      return sanitized;
    } catch (e) {
      return outcomeQProp;
    }
  }, [outcomeQProp]);

  const questionIdToPath = useMemo(
    () => buildQuestionIdToPath(questionsSource?.dimensions || []),
    [questionsSource?.dimensions]
  );
  const outcomeQuestionSetByOutcomeName = useMemo(
    () => buildOutcomeQuestionSetByOutcomeName(outcomeQ),
    [outcomeQ]
  );
  const lviDeps = useMemo(
    () => ({ questionIdToPath, outcomeQuestionSetByOutcomeName }),
    [questionIdToPath, outcomeQuestionSetByOutcomeName]
  );

  const categories = useMemo(() => core_data?.categories?.[0]?.categories || [], [core_data?.categories]);

  const parseCsv = (text) => {
    const input = String(text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    if (!input.trim()) return { rows: [] };

    const rows = [];
    let row = [];
    let cur = "";
    let inQuotes = false;

    for (let i = 0; i < input.length; i++) {
      const ch = input[i];
      if (inQuotes) {
        if (ch === '"') {
          const next = input[i + 1];
          if (next === '"') {
            cur += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          cur += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ",") {
          row.push(cur);
          cur = "";
        } else if (ch === "\n") {
          row.push(cur);
          rows.push(row);
          row = [];
          cur = "";
        } else {
          cur += ch;
        }
      }
    }
    row.push(cur);
    rows.push(row);

    // Drop fully-empty trailing lines
    const trimmed = rows.filter((r) => r.some((c) => String(c || "").trim() !== ""));
    return { rows: trimmed };
  };

  const normalize = (s) => String(s || "").trim().toLowerCase();

  const validation = useMemo(() => {
    const parsed = parseCsv(csvText);
    const rows = parsed.rows || [];
    if (!rows.length) {
      return { ok: false, parsed, headerMap: [], issues: [], rowsSpec: [] };
    }

    const headerRow = rows[0] || [];
    const headerCells = headerRow.map((h) => String(h || "").trim()).filter((h) => h !== "");
    if (!headerCells.length) {
      return {
        ok: false,
        parsed,
        headerMap: [],
        issues: [{ type: "header", message: "Header row is empty." }],
        rowsSpec: [],
      };
    }

    const primary = categories.find((c) => String(c?.priority || "").toLowerCase() === "primary") || null;
    const issues = [];

    const groupHeader = String(headerRow?.[0] ?? "Group").trim() || "Group";
    const lastColIdx = Math.max(0, headerRow.length - 1);
    const tableHeader = String(headerRow?.[lastColIdx] ?? "Table Name").trim() || "Table Name";

    if (splitTablesByLastCol) {
      const minCols = groupMode ? 3 : 2; // group + >=1 anchor + table name OR >=1 anchor + table name
      if (headerRow.length < minCols) {
        return {
          ok: false,
          parsed,
          headerMap: [],
          issues: [
            {
              type: "table_col_required",
              message: `Table Name mode requires at least ${minCols} columns.`,
            },
          ],
          rowsSpec: [],
          groupMode,
          groupHeader,
          tableHeader,
        };
      }
    }

    const headerMap = headerRow.map((rawHeader, colIdx) => {
      if (groupMode && colIdx === 0) {
        return { colIdx, header: groupHeader, category: null, categoryId: null, isGroup: true };
      }
      if (splitTablesByLastCol && colIdx === lastColIdx) {
        return { colIdx, header: tableHeader, category: null, categoryId: null, isSpecial: true };
      }
      const h = String(rawHeader || "").trim();
      if (!h) return { colIdx, header: "", category: null, categoryId: null };

      let cat =
        normalize(h) === "overview"
          ? primary
          : categories.find((c) => normalize(c?.name) === normalize(h)) || null;

      if (!cat) {
        issues.push({
          type: "unknown_parent",
          colIdx,
          header: h,
          message: `Unknown anchor parent "${h}" (no matching category).`,
        });
      }
      return { colIdx, header: h, category: cat, categoryId: cat?.id ?? null };
    });

    // Duplicate parent mapping check (ignore empty headers)
    const seen = new Map();
    headerMap.forEach((h) => {
      if (!h?.categoryId) return;
      const key = String(h.categoryId);
      const prev = seen.get(key);
      if (prev) {
        issues.push({
          type: "duplicate_parent",
          message: `Duplicate anchor parent columns: "${prev.header}" and "${h.header}".`,
        });
      } else {
        seen.set(key, h);
      }
    });

    const dataRows = rows.slice(1);
    const rowsSpec = dataRows.map((r, rowIdx0) => {
      const rowIdx = rowIdx0 + 2; // 1-based line number in CSV including header
      const filters = [];
      const display = [];
      const groupId = groupMode ? String(r?.[0] ?? "").trim() : null;
      const tableKey = splitTablesByLastCol ? String(r?.[lastColIdx] ?? "").trim() : null;
      if (groupMode && !groupId) {
        issues.push({
          type: "missing_group_id",
          rowIdx,
          message: `Row ${rowIdx}: missing Group ID in column 1.`,
        });
      }
      if (splitTablesByLastCol && !tableKey) {
        issues.push({
          type: "missing_table_key",
          rowIdx,
          message: `Row ${rowIdx}: missing ${tableHeader || "Table Name"} in the last column.`,
        });
      }

      headerMap.forEach((h) => {
        if (h?.isGroup || h?.isSpecial) return;
        const raw = r?.[h.colIdx];
        const cell = String(raw ?? "").trim();
        display.push(cell);

        if (!cell) return;
        if (!h.category) {
          // header issue already recorded; don't spam per-cell errors
          return;
        }
        const options = Array.isArray(h.category?.options) ? h.category.options : [];
        const matches = options.filter((o) => normalize(o?.name) === normalize(cell));
        if (matches.length === 0) {
          issues.push({
            type: "unmatched_anchor",
            rowIdx,
            colIdx: h.colIdx,
            header: h.header,
            value: cell,
            message: `Row ${rowIdx}${groupMode && groupId ? ` (Group ${groupId})` : ""}: "${cell}" not found under "${h.header}".`,
          });
          return;
        }
        if (matches.length > 1) {
          issues.push({
            type: "ambiguous_anchor",
            rowIdx,
            colIdx: h.colIdx,
            header: h.header,
            value: cell,
            message: `Row ${rowIdx}${groupMode && groupId ? ` (Group ${groupId})` : ""}: "${cell}" is ambiguous under "${h.header}" (multiple options match).`,
          });
          return;
        }
        filters.push({
          categoryId: h.categoryId,
          optionId: matches[0].id,
          header: h.header,
          value: cell,
        });
      });

      return { rowIdx, rawRow: r, filters, display, groupId, tableKey };
    });

    const ok = issues.length === 0 && rowsSpec.length > 0;
    return { ok, parsed, headerMap, issues, rowsSpec, groupMode, groupHeader, tableHeader };
  }, [csvText, categories, groupMode, splitTablesByLastCol]);

  const QUADRANT_COLORS = useMemo(
    () => ({
      [QUADRANT_KEYS.TL]: "#2D70E2",
      [QUADRANT_KEYS.TR]: "#10B981",
      [QUADRANT_KEYS.BL]: "#F59E0B",
      [QUADRANT_KEYS.BR]: "#7F56D9",
      [QUADRANT_KEYS.SUB]: "#EF4444",
      noScore: "#C7CFDB",
    }),
    []
  );
  const QUADRANT_LABELS = useMemo(
    () => ({
      [QUADRANT_KEYS.TR]: "Champion",
      [QUADRANT_KEYS.TL]: "Supportive",
      [QUADRANT_KEYS.BR]: "Situational",
      [QUADRANT_KEYS.BL]: "Skeptic",
      [QUADRANT_KEYS.SUB]: "Disconnected",
    }),
    []
  );

  const compute = async () => {
    if (!validation.ok) return;
    if (!correctedData || !dim1Def || !dim2Def) return;

    setIsComputing(true);
    await new Promise((r) => setTimeout(r, 0));

    const filterRespondentsForSpec = (spec) =>
      (correctedData || []).filter((resp) => {
        // Match all specified filters (AND)
        return (spec.filters || []).every((f) => {
          const match = resp?.categories?.find((c) => String(c.id) === String(f.categoryId));
          return match && String(match.response) === String(f.optionId);
        });
      });

    const unionRespondents = (lists) => {
      const out = [];
      const seen = new Set();
      (lists || []).forEach((arr) => {
        (arr || []).forEach((resp) => {
          const keyVal =
            resp?.employee ??
            resp?.employee_id ??
            resp?.employeeId ??
            resp?.id ??
            resp?.emp_id ??
            null;
          const key = keyVal != null ? `id:${keyVal}` : resp; // fallback to object identity
          if (seen.has(key)) return;
          seen.add(key);
          out.push(resp);
        });
      });
      return out;
    };

    const buildResultRow = ({ id, label, display, respondents, tableKey }) => {
      const respondentsCount = respondents.length;
      const belowPrivacyThreshold = respondentsCount < 5;

      let dim1ScorePct = null;
      let dim2ScorePct = null;
      let quadrant = null;
      let bottom2Count = 0;
      let bottom2Pct = 0;
      let risk = { label: "--", key: "none", color: "#9CA3AF" };

      if (!belowPrivacyThreshold) {
        dim1ScorePct = scoreAnchorOptionForDimPercent(respondents, dim1Def, minResponses, lviDeps);
        dim2ScorePct = scoreAnchorOptionForDimPercent(respondents, dim2Def, minResponses, lviDeps);
        quadrant = computeQuadrantInclusionForRespondents(respondents, dim1Def, dim2Def, quadrantConfig, lviDeps);
        bottom2Count =
          Number(quadrant?.counts?.[QUADRANT_KEYS.BL] || 0) + Number(quadrant?.counts?.[QUADRANT_KEYS.SUB] || 0);
        bottom2Pct =
          Number(quadrant?.percents?.[QUADRANT_KEYS.BL] || 0) + Number(quadrant?.percents?.[QUADRANT_KEYS.SUB] || 0);
        risk = riskGradeForBottom2(bottom2Pct, riskThresholds);
      }

      return {
        id,
        label,
        display: Array.isArray(display) ? display : [],
        tableKey: tableKey ?? null,
        respondentsCount,
        belowPrivacyThreshold,
        dim1ScorePct,
        dim2ScorePct,
        quadrantEligibleCount: quadrant?.eligibleCount ?? 0,
        qTRPct: quadrant?.percents?.[QUADRANT_KEYS.TR] ?? null,
        qTLPct: quadrant?.percents?.[QUADRANT_KEYS.TL] ?? null,
        qBRPct: quadrant?.percents?.[QUADRANT_KEYS.BR] ?? null,
        qBLPct: quadrant?.percents?.[QUADRANT_KEYS.BL] ?? null,
        qSUBPct: quadrant?.percents?.[QUADRANT_KEYS.SUB] ?? null,
        qCounts: quadrant?.counts ?? null,
        qBottom2Pct: belowPrivacyThreshold ? null : bottom2Pct,
        qBottom2Count: belowPrivacyThreshold ? null : bottom2Count,
        riskLabel: belowPrivacyThreshold ? "Insufficient data" : risk.label,
        riskKey: risk.key,
        riskColor: belowPrivacyThreshold ? "#9CA3AF" : risk.color,
      };
    };

    let results = [];
    if (groupMode) {
      const byGroup = new Map();
      (validation.rowsSpec || []).forEach((spec) => {
        const gid = String(spec?.groupId ?? "").trim();
        const tk = splitTablesByLastCol ? String(spec?.tableKey ?? "").trim() : "";
        const key = splitTablesByLastCol ? `t:${tk}::g:${gid}` : gid;
        const arr = byGroup.get(key) || [];
        arr.push(spec);
        byGroup.set(key, arr);
      });

      const groupAnchorHeaders = (validation?.headerMap || []).filter((h) => !h?.isGroup && !h?.isSpecial && h?.header);
      const anchorColCount = groupAnchorHeaders.length;

      results = Array.from(byGroup.entries()).map(([key, specs], idx) => {
        const gid = String(specs?.[0]?.groupId ?? "").trim();
        const tk = splitTablesByLastCol ? String(specs?.[0]?.tableKey ?? "").trim() : null;
        const cohorts = (specs || []).map((s) => filterRespondentsForSpec(s));
        const respondents = unionRespondents(cohorts);
        const display = (() => {
          const cols = [];
          for (let colIdx = 0; colIdx < anchorColCount; colIdx++) {
            const set = new Set(
              (specs || [])
                .map((s) => String(s?.display?.[colIdx] ?? "").trim())
                .filter(Boolean)
            );
            const values = Array.from(set);
            values.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
            cols.push(values.join(", "));
          }
          return cols;
        })();
        return buildResultRow({
          id: `${splitTablesByLastCol ? `t:${tk || "unknown"}:` : ""}g:${gid || "unknown"}:${idx}`,
          label: gid || "—",
          display,
          respondents,
          tableKey: tk,
        });
      });
    } else {
      results = (validation.rowsSpec || []).map((spec, idx) => {
        const respondents = filterRespondentsForSpec(spec);
        return buildResultRow({
          id: `${spec.rowIdx}-${idx}`,
          label: (spec.display || []).join(" • "),
          display: spec.display || [],
          respondents,
          tableKey: splitTablesByLastCol ? String(spec?.tableKey ?? "").trim() : null,
        });
      });
    }

    setRowsComputed(results);
    setIsComputing(false);
  };

  const rowsByTableKey = useMemo(() => {
    if (!splitTablesByLastCol) return null;
    const m = new Map();
    (rowsComputed || []).forEach((r) => {
      const key = String(r?.tableKey ?? "").trim() || "—";
      const arr = m.get(key) || [];
      arr.push(r);
      m.set(key, arr);
    });
    return m;
  }, [rowsComputed, splitTablesByLastCol]);

  const tableKeys = useMemo(() => {
    if (!splitTablesByLastCol || !rowsByTableKey) return [];
    const keys = Array.from(rowsByTableKey.keys());
    keys.sort((a, b) => String(a).toLowerCase().localeCompare(String(b).toLowerCase()));
    return keys;
  }, [rowsByTableKey, splitTablesByLastCol]);

  const effectiveActiveTableKey = useMemo(() => {
    if (!splitTablesByLastCol) return "";
    if (activeTableKey && rowsByTableKey?.has(activeTableKey)) return activeTableKey;
    return tableKeys[0] || "";
  }, [splitTablesByLastCol, activeTableKey, rowsByTableKey, tableKeys]);

  useEffect(() => {
    if (!splitTablesByLastCol) return;
    if (!tableKeys.length) return;
    if (activeTableKey && tableKeys.includes(activeTableKey)) return;
    setActiveTableKey(tableKeys[0]);
  }, [splitTablesByLastCol, tableKeys, activeTableKey]);

  const visibleRowsComputed = useMemo(() => {
    if (!splitTablesByLastCol) return rowsComputed || [];
    return rowsByTableKey?.get(effectiveActiveTableKey) || [];
  }, [splitTablesByLastCol, rowsComputed, rowsByTableKey, effectiveActiveTableKey]);

  const sortedRows = useMemo(() => {
    const dir = String(sortDir) === "asc" ? 1 : -1;
    const asNumberOrNull = (v) => (v == null || isNaN(Number(v)) ? null : Number(v));
    const asString = (v) => String(v ?? "").toLowerCase();

    const groupAnchorHeaders = (validation?.headerMap || []).filter((h) => !h?.isGroup && !h?.isSpecial && h?.header);
    const anchorCols = groupAnchorHeaders.length;
    const key = String(sortKey || "risk");

    const getVal = (row) => {
      if (key === "group") return asString(row?.label);
      if (key.startsWith("a:")) {
        const idx = Number(key.slice(2));
        return asString(row?.display?.[idx]);
      }
      switch (key) {
        case "n":
          return asNumberOrNull(row?.respondentsCount);
        case "x":
          return asNumberOrNull(row?.dim1ScorePct);
        case "tr":
          return asNumberOrNull(row?.qTRPct);
        case "tl":
          return asNumberOrNull(row?.qTLPct);
        case "br":
          return asNumberOrNull(row?.qBRPct);
        case "bl":
          return asNumberOrNull(row?.qBLPct);
        case "sub":
          return asNumberOrNull(row?.qSUBPct);
        case "risk":
        default:
          // Risk derived from Bottom 2% (BL + SUB). Null when below privacy threshold.
          return asNumberOrNull(row?.qBottom2Pct);
      }
    };

    const isTextSort = key === "group" || key.startsWith("a:");
    const isValidAnchorKey = key.startsWith("a:") && Number(key.slice(2)) >= 0 && Number(key.slice(2)) < anchorCols;
    if (key.startsWith("a:") && !isValidAnchorKey) return [...(visibleRowsComputed || [])];

    return [...(visibleRowsComputed || [])].sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);

      // Nulls last
      if (av == null && bv == null) return asString(a?.label).localeCompare(asString(b?.label));
      if (av == null) return 1;
      if (bv == null) return -1;

      if (isTextSort) {
        if (av !== bv) return av.localeCompare(bv) * dir;
        return asString(a?.label).localeCompare(asString(b?.label));
      }

      if (av !== bv) return (av - bv) * dir;
      return asString(a?.label).localeCompare(asString(b?.label));
    });
  }, [visibleRowsComputed, sortKey, sortDir, validation?.headerMap?.length]);

  const onSort = (key) => {
    const nextKey = key || "risk";
    setSortKey((prev) => {
      if (prev === nextKey) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDir(nextKey === "name" || String(nextKey).startsWith("a:") ? "asc" : "desc");
      return nextKey;
    });
  };

  const onHeaderKeyDown = (e, key) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSort(key);
    }
  };

  const csvEscape = (value) => {
    const s = value == null ? "" : String(value);
    const needsQuotes = /[",\n\r]/.test(s);
    const escaped = s.replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };

  const downloadCsv = () => {
    const groupAnchorHeaders = (validation?.headerMap || []).filter((h) => !h?.isGroup && !h?.isSpecial && h?.header);
    const headers = [
      ...(groupMode
        ? [validation?.groupHeader || "Group", ...groupAnchorHeaders.map((h) => h.header || "")]
        : groupAnchorHeaders.map((h) => h.header || "")),
      "Respondents (n)",
      `${dim1Name} Score (%)`,
      "Champion (%)",
      "Supportive (%)",
      "Situational (%)",
      "Skeptic (%)",
      "Disconnected (%)",
      "Risk Grade",
    ];

    const rows = (sortedRows || []).map((r) => [
      ...(groupMode
        ? [r.label, ...(r.display || []).map((c) => c ?? "")]
        : (r.display || []).map((c) => c ?? "")),
      r.respondentsCount ?? "",
      r.dim1ScorePct == null ? "" : Math.round(r.dim1ScorePct),
      r.qTRPct == null ? "" : Math.round(r.qTRPct),
      r.qTLPct == null ? "" : Math.round(r.qTLPct),
      r.qBRPct == null ? "" : Math.round(r.qBRPct),
      r.qBLPct == null ? "" : Math.round(r.qBLPct),
      r.qSUBPct == null ? "" : Math.round(r.qSUBPct),
      r.riskLabel || "",
    ]);

    const csv = [headers.map(csvEscape).join(","), ...rows.map((cols) => cols.map(csvEscape).join(","))].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const safeName = (s) =>
      String(s || "")
        .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 120);

    const base = `${safeName("Bulk LVI")} - ${safeName(core_data?.organization_name || "export")}`.trim();
    const tablePart =
      splitTablesByLastCol && effectiveActiveTableKey ? ` - ${safeName(effectiveActiveTableKey)}` : "";
    const filename = `${base}${tablePart}.csv` || "bulk-lvi.csv";
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const goBackToLvi = () => {
    const dataset = searchParams.get("dataset");
    const qs = dataset ? `?dataset=${encodeURIComponent(dataset)}` : "";
    navigate(`/leader/audit/simple/custom-analytics${qs}`);
  };

  // IMPORTANT: Hooks must run unconditionally; block rendering only after all hooks are declared.
  if (!isAuthorized) return null;

  if (!core_data) return <div>Loading...</div>;
  if (!analyticsSchema || !analysisDef) {
    return (
      <Container>
        <AnalyticsBreadcrumb currentComponent="custom" />
        <HeaderRow>
          <Title>
            <TitleIconWrapper aria-hidden>
              <QuadrantIcon />
            </TitleIconWrapper>
            Bulk LVI (CSV)
          </Title>
        </HeaderRow>
        <EmptyState>Analytics schema not found for this survey.</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <AnalyticsBreadcrumb currentComponent="custom" />
      <HeaderRow>
        <Title>
          <TitleIconWrapper aria-hidden>
            <QuadrantIcon />
          </TitleIconWrapper>
          Bulk LVI (CSV)
        </Title>
      </HeaderRow>

      <OptionsContainer>
        <TopLeftActions>
          <GhostButton type="button" onClick={goBackToLvi}>
            Back to LVI
          </GhostButton>
        </TopLeftActions>
        <TopRightActions>
          <GhostButton type="button" onClick={() => setShowSettings(true)}>
            Settings
          </GhostButton>
        </TopRightActions>
      </OptionsContainer>

      <InlineSectionTitle>Filters (applied to every row)</InlineSectionTitle>
      <FiltersRow>
        <Filters />
      </FiltersRow>

      <Content>
        <Section>
          <SectionHeader>
            <SectionTitle>Paste CSV</SectionTitle>
            <Actions>
              <CsvButton
                type="button"
                onClick={downloadCsv}
                disabled={!sortedRows.length}
                title={sortedRows.length ? "Download CSV" : "Compute results first"}
              >
                Save CSV
              </CsvButton>
              <PrimaryButton
                type="button"
                onClick={compute}
                disabled={!validation.ok || isComputing || !correctedData}
                title={!validation.ok ? "Fix CSV issues first" : "Compute"}
              >
                {isComputing ? "Calculating…" : "Compute"}
              </PrimaryButton>
            </Actions>
          </SectionHeader>

          <HelpText>
            First row must be the anchor parent (category) names. Each subsequent row is one cohort, filtering by the anchor
            option names in that row. Any unmatched/ambiguous names will be listed below and calculation will be blocked. Optional:
            enable “Last column is Table Name” to split results into 1 table per unique value (and export only the selected table).
          </HelpText>

          <ToggleRow>
            <ToggleLabel>
              <input
                type="checkbox"
                checked={groupMode}
                onChange={(e) => {
                  setGroupMode(!!e.target.checked);
                  setRowsComputed([]);
                  setSortKey("risk");
                  setSortDir("desc");
                  setActiveTableKey("");
                }}
              />
              <span>First column is Group ID (union rows per group)</span>
            </ToggleLabel>
          </ToggleRow>
          <ToggleRow>
            <ToggleLabel>
              <input
                type="checkbox"
                checked={splitTablesByLastCol}
                onChange={(e) => {
                  setSplitTablesByLastCol(!!e.target.checked);
                  setRowsComputed([]);
                  setSortKey("risk");
                  setSortDir("desc");
                  setActiveTableKey("");
                }}
              />
              <span>Last column is Table Name (create 1 table per unique value)</span>
            </ToggleLabel>
          </ToggleRow>

          <CsvArea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={`Example:\nStore,Store Location,Ontario Region\n0026,Store Location X,Ontario Region\n\nWith Table Name:\nStore,Store Location,Ontario Region,Table Name\n0026,Store Location X,Ontario Region,Jane Doe`}
            spellCheck={false}
          />

          {validation.issues?.length > 0 && (
            <IssuesBox>
              <IssuesTitle>CSV issues</IssuesTitle>
              <IssuesList>
                {validation.issues.slice(0, 200).map((it, idx) => (
                  <li key={`${it.type}-${idx}`}>{it.message}</li>
                ))}
                {validation.issues.length > 200 && (
                  <li>…and {validation.issues.length - 200} more</li>
                )}
              </IssuesList>
            </IssuesBox>
          )}
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>Results</SectionTitle>
            <ResultsHeaderRight>
              {splitTablesByLastCol && tableKeys.length > 0 && (
                <TablePicker>
                  <TablePickerLabel>{validation?.tableHeader || "Table Name"}</TablePickerLabel>
                  <TablePickerSelect value={effectiveActiveTableKey} onChange={(e) => setActiveTableKey(e.target.value)}>
                    {tableKeys.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </TablePickerSelect>
                </TablePicker>
              )}
              <SummaryMeta>
                {splitTablesByLastCol ? (
                  <>
                    {sortedRows.length} rows{rowsComputed.length !== sortedRows.length ? ` (of ${rowsComputed.length})` : ""}
                  </>
                ) : (
                  <>{rowsComputed.length} rows</>
                )}
                {isComputing ? " • Calculating…" : ""}
              </SummaryMeta>
            </ResultsHeaderRight>
          </SectionHeader>

          <TableWrap>
            {isComputing && (
              <TableOverlay>
                <Spinner />
                <OverlayText>Calculating…</OverlayText>
              </TableOverlay>
            )}
            <Table>
              <thead>
                <tr>
                  {(() => {
                    const groupAnchorHeaders = (validation?.headerMap || []).filter((h) => !h?.isGroup && !h?.isSpecial && h?.header);

                    if (groupMode) {
                      return (
                        <>
                          <Th onClick={() => onSort("group")} onKeyDown={(e) => onHeaderKeyDown(e, "group")} role="button" tabIndex={0}>
                            <ThInner>
                              <HeaderText>{validation?.groupHeader || "Group"}</HeaderText>
                              <SortHint active={sortKey === "group"}>
                                {sortKey === "group" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                              </SortHint>
                            </ThInner>
                          </Th>
                          {groupAnchorHeaders.map((h, idx) => (
                            <Th
                              key={`ga-${idx}`}
                              onClick={() => onSort(`a:${idx}`)}
                              onKeyDown={(e) => onHeaderKeyDown(e, `a:${idx}`)}
                              role="button"
                              tabIndex={0}
                            >
                              <ThInner>
                                <HeaderText>{h?.header || `Col ${idx + 1}`}</HeaderText>
                                <SortHint active={sortKey === `a:${idx}`}>
                                  {sortKey === `a:${idx}` ? (sortDir === "asc" ? "↑" : "↓") : ""}
                                </SortHint>
                              </ThInner>
                            </Th>
                          ))}
                        </>
                      );
                    }

                    return groupAnchorHeaders.map((h, idx) => (
                      <Th
                        key={`h-${idx}`}
                        onClick={() => onSort(`a:${idx}`)}
                        onKeyDown={(e) => onHeaderKeyDown(e, `a:${idx}`)}
                        role="button"
                        tabIndex={0}
                      >
                        <ThInner>
                          <HeaderText>{h.header || `Col ${idx + 1}`}</HeaderText>
                          <SortHint active={sortKey === `a:${idx}`}>{sortKey === `a:${idx}` ? (sortDir === "asc" ? "↑" : "↓") : ""}</SortHint>
                        </ThInner>
                      </Th>
                    ));
                  })()}
                  <ThRight onClick={() => onSort("n")} onKeyDown={(e) => onHeaderKeyDown(e, "n")} role="button" tabIndex={0}>
                    <ThInner>
                      <HeaderText>n</HeaderText>
                      <SortHint active={sortKey === "n"}>{sortKey === "n" ? (sortDir === "asc" ? "↑" : "↓") : ""}</SortHint>
                    </ThInner>
                  </ThRight>
                  <ThRightHighlight onClick={() => onSort("x")} onKeyDown={(e) => onHeaderKeyDown(e, "x")} role="button" tabIndex={0}>
                    <ThInner>
                      <HeaderText>{dim1Name} Score (%)</HeaderText>
                      <SortHint active={sortKey === "x"}>{sortKey === "x" ? (sortDir === "asc" ? "↑" : "↓") : ""}</SortHint>
                    </ThInner>
                  </ThRightHighlight>
                  <ThRight onClick={() => onSort("tr")} onKeyDown={(e) => onHeaderKeyDown(e, "tr")} role="button" tabIndex={0}>
                    <ThInner>
                      <HeaderText>Champion %</HeaderText>
                      <SortHint active={sortKey === "tr"}>{sortKey === "tr" ? (sortDir === "asc" ? "↑" : "↓") : ""}</SortHint>
                    </ThInner>
                  </ThRight>
                  <ThRight onClick={() => onSort("tl")} onKeyDown={(e) => onHeaderKeyDown(e, "tl")} role="button" tabIndex={0}>
                    <ThInner>
                      <HeaderText>Supportive %</HeaderText>
                      <SortHint active={sortKey === "tl"}>{sortKey === "tl" ? (sortDir === "asc" ? "↑" : "↓") : ""}</SortHint>
                    </ThInner>
                  </ThRight>
                  <ThRight onClick={() => onSort("br")} onKeyDown={(e) => onHeaderKeyDown(e, "br")} role="button" tabIndex={0}>
                    <ThInner>
                      <HeaderText>Situational %</HeaderText>
                      <SortHint active={sortKey === "br"}>{sortKey === "br" ? (sortDir === "asc" ? "↑" : "↓") : ""}</SortHint>
                    </ThInner>
                  </ThRight>
                  <ThRight onClick={() => onSort("bl")} onKeyDown={(e) => onHeaderKeyDown(e, "bl")} role="button" tabIndex={0}>
                    <ThInner>
                      <HeaderText>Skeptic %</HeaderText>
                      <SortHint active={sortKey === "bl"}>{sortKey === "bl" ? (sortDir === "asc" ? "↑" : "↓") : ""}</SortHint>
                    </ThInner>
                  </ThRight>
                  <ThRight onClick={() => onSort("sub")} onKeyDown={(e) => onHeaderKeyDown(e, "sub")} role="button" tabIndex={0}>
                    <ThInner>
                      <HeaderText>Disconnected %</HeaderText>
                      <SortHint active={sortKey === "sub"}>{sortKey === "sub" ? (sortDir === "asc" ? "↑" : "↓") : ""}</SortHint>
                    </ThInner>
                  </ThRight>
                  <Th onClick={() => onSort("risk")} onKeyDown={(e) => onHeaderKeyDown(e, "risk")} role="button" tabIndex={0}>
                    <ThInner>
                      <HeaderText>Risk</HeaderText>
                      <SortHint active={sortKey === "risk"}>{sortKey === "risk" ? (sortDir === "asc" ? "↑" : "↓") : ""}</SortHint>
                    </ThInner>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {(sortedRows || []).map((row) => (
                  <Tr key={row.id}>
                    {groupMode ? (
                      <>
                        <Td title={row.label}>{row.label || "--"}</Td>
                        {(row.display || []).map((c, idx) => (
                          <Td key={`g-${row.id}-${idx}`} title={String(c ?? "")}>
                            {String(c ?? "") || "--"}
                          </Td>
                        ))}
                      </>
                    ) : (
                      (row.display || []).map((c, idx) => (
                        <Td key={`c-${row.id}-${idx}`}>{c || "--"}</Td>
                      ))
                    )}
                    <TdRight>{row.respondentsCount ?? "--"}</TdRight>
                    <TdRightHighlight>
                      {row.belowPrivacyThreshold ? "--" : row.dim1ScorePct == null ? "--" : `${Math.round(row.dim1ScorePct)}%`}
                    </TdRightHighlight>
                    <TdRight>{row.belowPrivacyThreshold ? "--" : row.qTRPct == null ? "--" : `${Math.round(row.qTRPct)}%`}</TdRight>
                    <TdRight>{row.belowPrivacyThreshold ? "--" : row.qTLPct == null ? "--" : `${Math.round(row.qTLPct)}%`}</TdRight>
                    <TdRight>{row.belowPrivacyThreshold ? "--" : row.qBRPct == null ? "--" : `${Math.round(row.qBRPct)}%`}</TdRight>
                    <TdRight>{row.belowPrivacyThreshold ? "--" : row.qBLPct == null ? "--" : `${Math.round(row.qBLPct)}%`}</TdRight>
                    <TdRight>{row.belowPrivacyThreshold ? "--" : row.qSUBPct == null ? "--" : `${Math.round(row.qSUBPct)}%`}</TdRight>
                    <Td>
                      <RiskLabel style={{ color: row.riskColor || "#9CA3AF" }}>{row.riskLabel || "--"}</RiskLabel>
                      {row.belowPrivacyThreshold && <MetaLine>Need ≥ 5</MetaLine>}
                    </Td>
                  </Tr>
                ))}
                {!rowsComputed.length && (
                  <Tr>
                    <Td
                      colSpan={(
                        (groupMode
                          ? (1 + (validation?.headerMap || []).filter((h) => !h?.isGroup && !h?.isSpecial && h?.header).length)
                          : (validation.headerMap || []).filter((h) => !h?.isGroup && !h?.isSpecial).length
                        ) + 8
                      )}
                    >
                      <EmptyInline>No results yet. Paste CSV and click Compute.</EmptyInline>
                    </Td>
                  </Tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        </Section>
      </Content>

      {showSettings &&
        createPortal(
          <ModalOverlay
            role="presentation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setShowSettings(false);
            }}
          >
            <ModalCard role="dialog" aria-modal="true" aria-label="Analytics settings">
              <ModalHeader>
                <ModalTitle>Analytics Settings</ModalTitle>
                <ModalClose type="button" onClick={() => setShowSettings(false)}>
                  Close
                </ModalClose>
              </ModalHeader>
              <ModalBody>
                <ModalSection>
                  <ModalSectionTitle>Quadrants</ModalSectionTitle>
                  <SettingsGrid>
                    <ControlGroup>
                      <Label>{dim1Name} cutoff (X)</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={quadrantConfig.xCutoff}
                        onChange={(e) => {
                          const value = e?.target?.value;
                          setQuadrantConfig((prev) =>
                            sanitizeQuadrantConfig({
                              ...prev,
                              xCutoff: value,
                              subRect: prev.subRect,
                            })
                          );
                        }}
                      />
                    </ControlGroup>
                    <ControlGroup>
                      <Label>{dim2Name} cutoff (Y)</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={quadrantConfig.yCutoff}
                        onChange={(e) => {
                          const value = e?.target?.value;
                          setQuadrantConfig((prev) =>
                            sanitizeQuadrantConfig({
                              ...prev,
                              yCutoff: value,
                              subRect: prev.subRect,
                            })
                          );
                        }}
                      />
                    </ControlGroup>
                  </SettingsGrid>
                </ModalSection>

                <ModalSection>
                  <ModalSectionTitle>Sub-quadrant (inside BL, exclusive)</ModalSectionTitle>
                  <SubRow>
                    <MiniField>
                      <MiniLabel>Xmin</MiniLabel>
                      <MiniInput
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={quadrantConfig.subRect.xmin}
                        onChange={(e) => {
                          const value = e?.target?.value;
                          setQuadrantConfig((prev) =>
                            sanitizeQuadrantConfig({
                              ...prev,
                              subRect: { ...prev.subRect, xmin: value },
                            })
                          );
                        }}
                      />
                    </MiniField>
                    <MiniField>
                      <MiniLabel>Xmax</MiniLabel>
                      <MiniInput
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={quadrantConfig.subRect.xmax}
                        onChange={(e) => {
                          const value = e?.target?.value;
                          setQuadrantConfig((prev) =>
                            sanitizeQuadrantConfig({
                              ...prev,
                              subRect: { ...prev.subRect, xmax: value },
                            })
                          );
                        }}
                      />
                    </MiniField>
                    <MiniField>
                      <MiniLabel>Ymin</MiniLabel>
                      <MiniInput
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={quadrantConfig.subRect.ymin}
                        onChange={(e) => {
                          const value = e?.target?.value;
                          setQuadrantConfig((prev) =>
                            sanitizeQuadrantConfig({
                              ...prev,
                              subRect: { ...prev.subRect, ymin: value },
                            })
                          );
                        }}
                      />
                    </MiniField>
                    <MiniField>
                      <MiniLabel>Ymax</MiniLabel>
                      <MiniInput
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={quadrantConfig.subRect.ymax}
                        onChange={(e) => {
                          const value = e?.target?.value;
                          setQuadrantConfig((prev) =>
                            sanitizeQuadrantConfig({
                              ...prev,
                              subRect: { ...prev.subRect, ymax: value },
                            })
                          );
                        }}
                      />
                    </MiniField>
                  </SubRow>
                </ModalSection>

                <ModalSection>
                  <ModalSectionTitle>Risk thresholds (Bottom 2%)</ModalSectionTitle>
                  <SubRow>
                    <MiniField>
                      <MiniLabel>Very High ≥</MiniLabel>
                      <MiniInput
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={riskThresholds.veryHigh}
                        onChange={(e) => setRiskThresholds((prev) => ({ ...prev, veryHigh: Number(e?.target?.value) }))}
                      />
                    </MiniField>
                    <MiniField>
                      <MiniLabel>High ≥</MiniLabel>
                      <MiniInput
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={riskThresholds.high}
                        onChange={(e) => setRiskThresholds((prev) => ({ ...prev, high: Number(e?.target?.value) }))}
                      />
                    </MiniField>
                    <MiniField>
                      <MiniLabel>Moderate ≥</MiniLabel>
                      <MiniInput
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={riskThresholds.moderate}
                        onChange={(e) => setRiskThresholds((prev) => ({ ...prev, moderate: Number(e?.target?.value) }))}
                      />
                    </MiniField>
                    <MiniField>
                      <MiniLabel>Stable ≥</MiniLabel>
                      <MiniInput
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={riskThresholds.stable}
                        onChange={(e) => setRiskThresholds((prev) => ({ ...prev, stable: Number(e?.target?.value) }))}
                      />
                    </MiniField>
                    <MiniField>
                      <MiniLabel>Low ≥</MiniLabel>
                      <MiniInput
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={riskThresholds.low}
                        onChange={(e) => setRiskThresholds((prev) => ({ ...prev, low: Number(e?.target?.value) }))}
                      />
                    </MiniField>
                  </SubRow>
                </ModalSection>
              </ModalBody>
            </ModalCard>
          </ModalOverlay>,
          document.body
        )}
    </Container>
  );
};

export default BulkLviFromCsv;

const QuadrantIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="3.5" stroke="#1F2937" strokeWidth="1.6" />
    <path d="M12 3v18M3 12h18" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="16.5" cy="7.5" r="1.6" fill="#1F2937" opacity="0.85" />
    <circle cx="7.5" cy="16.5" r="1.6" fill="#1F2937" opacity="0.85" />
  </svg>
);

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: "Raleway";
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.div`
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 18px;
  color: #2a3039;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 7px;
  color: #1f2937;
`;

const OptionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  min-height: 40px;
`;

const TopLeftActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TopRightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const InlineSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #2a3039;
  margin-top: 10px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div`
  width: 100%;
  padding: 0 4px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2a3039;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 10px 0;
`;

const CsvArea = styled.textarea`
  width: 100%;
  min-height: 180px;
  resize: vertical;
  background: #ffffff;
  border: 1px solid #e1e5e9;
  border-radius: 10px;
  padding: 12px;
  font-size: 12px;
  color: #2a3039;
  line-height: 1.4;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 8px 0 10px 0;
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #2a3039;

  input {
    width: 14px;
    height: 14px;
    accent-color: #2d70e2;
  }
`;

const IssuesBox = styled.div`
  margin-top: 12px;
  border: 1px solid #fee2e2;
  background: #fff7f7;
  border-radius: 10px;
  padding: 12px;
`;

const IssuesTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #991b1b;
  margin-bottom: 6px;
`;

const IssuesList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: #7f1d1d;
  font-size: 12px;
  line-height: 1.35;
`;

const CsvButton = styled.button`
  background: #ffffff;
  border: 1px solid #e1e5e9;
  color: #2a3039;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: #c7cdd6;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const GhostButton = styled(CsvButton)`
  padding: 8px 12px;
`;

const PrimaryButton = styled(CsvButton)`
  background: #2d70e2;
  border-color: #2d70e2;
  color: #ffffff;

  &:hover {
    border-color: #2563eb;
    background: #2563eb;
  }
`;

const SummaryMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ResultsHeaderRight = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;

const TablePicker = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const TablePickerLabel = styled.span`
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  white-space: nowrap;
`;

const TablePickerSelect = styled.select`
  background: #ffffff;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 12px;
  color: #2a3039;
  min-width: 220px;
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid #e1e5e9;
  border-radius: 10px;
  position: relative;
  margin-top: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
`;

const TableOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(1px);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  z-index: 2;
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid #e1e5e9;
  border-top-color: #2d70e2;
  animation: spin 0.85s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const OverlayText = styled.div`
  font-size: 12px;
  color: #2a3039;
  font-weight: 600;
`;

const ThBase = styled.th`
  position: relative;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  color: #2a3039;
  padding: 8px 10px;
  border-bottom: 1px solid #e1e5e9;
  background: #ffffff;
  user-select: none;
  vertical-align: bottom;
  line-height: 1.15;
  white-space: nowrap;
  cursor: pointer;
`;

const Th = styled(ThBase)``;
const ThRight = styled(ThBase)`
  text-align: right;
`;
const ThRightHighlight = styled(ThRight)`
  background: #f3f4f6;
`;

const ThInner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const HeaderText = styled.span`
  white-space: nowrap;
`;

const SortHint = styled.span`
  color: ${(p) => (p.active ? "#2D70E2" : "#9CA3AF")};
  font-weight: 700;
  font-size: 12px;
  min-width: 14px;
  text-align: center;
`;

const Tr = styled.tr`
  &:hover td {
    background: #fbfcff;
  }
  &:hover td[data-highlight="true"] {
    background: #f3f4f6;
  }
`;

const Td = styled.td`
  padding: 12px 14px;
  border-bottom: 1px solid #f0f2f5;
  font-size: 12px;
  color: #2a3039;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
`;

const TdRight = styled(Td)`
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const TdRightHighlight = styled(TdRight).attrs({ "data-highlight": "true" })`
  background: #f3f4f6;
`;

const RiskLabel = styled.span`
  font-weight: 700;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 100%;
`;

const MetaLine = styled.div`
  color: #9ca3af;
  font-size: 11px;
  margin-top: 2px;
`;

const EmptyInline = styled.div`
  padding: 10px 0;
  color: #6b7280;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 200px;
  padding: 40px;
  color: #6b7280;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.28);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 18px 22px 22px 22px;
  z-index: 9999;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 760px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e1e5e9;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18);
  max-height: calc(100vh - 44px);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f6;
`;

const ModalTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #2a3039;
`;

const ModalClose = styled.button`
  background: #ffffff;
  border: 1px solid #e1e5e9;
  color: #2a3039;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 16px;
  overflow: auto;
`;

const ModalSection = styled.div`
  margin-bottom: 18px;
`;

const ModalSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #2a3039;
  margin-bottom: 10px;
`;

const SettingsGrid = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const ControlGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
`;

const Label = styled.span``;

const Input = styled.input`
  background: white;
  border: 1px solid #e1e5e9;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  color: #2a3039;
  width: 140px;
`;

const SubRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const MiniField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MiniLabel = styled.span`
  font-size: 10px;
  color: #9ca3af;
`;

const MiniInput = styled.input`
  background: white;
  border: 1px solid #e1e5e9;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 12px;
  color: #2a3039;
  width: 70px;
`;

